import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import * as api from "../api/index";
import {
  refreshItemsByIds,
  removeItemsByIds,
  resyncAllItems,
} from "../actions/itemActions";
import {
  ALL_CLIENTS,
  ALL_SUPPLIERS,
  ALL_STAFF,
  FETCH_EMPLOYEES,
  FETCH_ALL_STAFFCOMMISSIONDETAIL,
  FETCH_CATEGORIES,
  FETCH_UNITS,
  FETCH_ALL_MEETINGS,
} from "../actions/constants";
import useLiveData from "./useLiveData";
import { syncLiveSocket, disconnectLiveSocket } from "./liveSocket";

// Shared lists other screens read straight from Redux (POS customer picker,
// Purchase supplier picker, staff/commission on the POS, ...). Re-read
// silently - the regular getAll*() actions dispatch START_LOADING, which is
// shared by every reducer and would flash lists back to a spinner - and only
// when the list was already loaded (otherwise the screen loads it fresh).
const SHARED_LISTS = {
  categories: { loaded: (s) => s.categories.categories.length, fetch: api.fetchCategories, type: FETCH_CATEGORIES },
  units: { loaded: (s) => s.units.units.length, fetch: api.fetchUnits, type: FETCH_UNITS },
  mappings: { loaded: () => true, fetch: api.fetchUnitMappings, type: "FETCH_MAPPINGS" },
  clients: { loaded: (s) => s.clients.clients?.length, fetch: api.fetchAllClients, type: ALL_CLIENTS },
  suppliers: { loaded: (s) => s.suppliers.suppliers?.length, fetch: api.fetchAllSuppliers, type: ALL_SUPPLIERS },
  staff: { loaded: (s) => s.staff.staff?.length, fetch: api.fetchAllStaff, type: ALL_STAFF },
  employees: { loaded: (s) => s.employees.employees?.length, fetch: api.fetchEmployees, type: FETCH_EMPLOYEES },
  meetings: { loaded: (s) => s.meetings.meetings?.length, fetch: api.fetchMeetings, type: FETCH_ALL_MEETINGS },
  staffCommissions: {
    loaded: (s) => s.staffCommissionDetails.staffCommissionDetails?.length,
    fetch: api.fetchStaffCommissionDetails,
    type: FETCH_ALL_STAFFCOMMISSIONDETAIL,
  },
};

const refreshSharedList = (topic) => async (dispatch, getState) => {
  const list = SHARED_LISTS[topic];
  if (!list || !list.loaded(getState())) return;
  const { data } = await list.fetch();
  dispatch({ type: list.type, payload: data });
};

const logFailure = (what) => (error) =>
  console.warn(`Live update: failed to refresh ${what}`, error?.message || error);

/**
 * Mounted once (App). Keeps the socket in step with the login session and
 * keeps shared Redux data current, so the POS grid, Items list, pickers and
 * anything else reading these from the store update without a refresh.
 * Screens with their own data use useLiveData directly.
 */
export default function useLiveSync() {
  const dispatch = useDispatch();
  const location = useLocation();

  // Login/logout/token renewal all happen alongside navigation or a storage
  // write (another tab), so re-check the session on both.
  useEffect(() => {
    syncLiveSocket();
  }, [location]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === "profile" || event.key === null) syncLiveSocket();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      disconnectLiveSocket();
    };
  }, []);

  useLiveData(
    ["items", ...Object.keys(SHARED_LISTS)],
    (changes) => {
      // null = reconnected: re-read everything that's loaded.
      const items = changes ? changes.items : { all: true };
      if (items?.all) {
        dispatch(resyncAllItems()).catch(logFailure("items"));
      } else if (items) {
        if (items.ids.length) dispatch(refreshItemsByIds(items.ids)).catch(logFailure("items"));
        if (items.deleted.length) dispatch(removeItemsByIds(items.deleted));
      }
      Object.keys(SHARED_LISTS).forEach((topic) => {
        if (!changes || changes[topic]) {
          dispatch(refreshSharedList(topic)).catch(logFailure(topic));
        }
      });
    },
    // Store-level data backs the POS itself - keep it snappy.
    { minIntervalMs: 300 }
  );
}
